/**
 * SiteGlue "hire an AI employee" plan catalog for marketing pricing + billing comparison.
 * Stripe plan ids stay intern / part_time / full_time; labels are SiteGlue-facing.
 */

export type AiEmployeePlanId = "intern" | "associate" | "professional" | "enterprise";

/** Card is collected at signup. The selected plan price is charged on day 15. */
export const SITEGLUE_TRIAL_DAYS = 15;

/** Maps display plan → marketplace hire plan id (Stripe / hire wizard). */
export const AI_EMPLOYEE_TO_HIRE_PLAN: Record<
  Exclude<AiEmployeePlanId, "enterprise">,
  "intern" | "part_time" | "full_time"
> = {
  intern: "intern",
  associate: "part_time",
  professional: "full_time",
};

/** Stripe / hire-wizard plan id → SiteGlue Intern / Associate / Professional. */
export function aiEmployeePlanIdFromHirePlanId(
  hirePlanId: "intern" | "part_time" | "full_time",
): Exclude<AiEmployeePlanId, "enterprise"> {
  if (hirePlanId === "full_time") return "professional";
  if (hirePlanId === "part_time") return "associate";
  return "intern";
}

/**
 * Parse ?plan= from Pricing / marketing links.
 * Accepts SiteGlue ids (associate) and Stripe/hire ids (part_time).
 */
export function parseAiEmployeePlanIdFromParam(
  raw: string | null | undefined,
): Exclude<AiEmployeePlanId, "enterprise"> | null {
  const value = raw?.trim().toLowerCase();
  if (!value) return null;
  if (value === "intern" || value === "associate" || value === "professional") {
    return value;
  }
  if (value === "part_time") return "associate";
  if (value === "full_time") return "professional";
  return null;
}

export function marketplaceHirePlanIdFromParam(
  raw: string | null | undefined,
): "intern" | "part_time" | "full_time" | null {
  const plan = parseAiEmployeePlanIdFromParam(raw);
  return plan ? AI_EMPLOYEE_TO_HIRE_PLAN[plan] : null;
}

export function aiEmployeePlanPriceCents(planId: AiEmployeePlanId): number {
  if (planId === "intern") return 4900;
  if (planId === "associate") return 9900;
  if (planId === "professional") return 19900;
  return 0; // enterprise is custom
}

/** Header / checkout style label: FREE, $49/mo, $99/mo, Custom. */
export function formatAiEmployeePlanPriceLabel(planId: AiEmployeePlanId): string {
  if (planId === "enterprise") return "Custom";
  const cents = aiEmployeePlanPriceCents(planId);
  if (cents === 0) return "FREE";
  return `$${Math.round(cents / 100)}/mo`;
}

export function formatAiEmployeePlanHeaderLabel(planId: AiEmployeePlanId): string {
  const plan = AI_EMPLOYEE_PLANS.find((p) => p.id === planId);
  const name = plan?.name ?? "Plan";
  const price = formatAiEmployeePlanPriceLabel(planId);
  if (planId === "intern") return `${name} · ${price}`;
  if (planId === "enterprise") return `${name} · ${price}`;
  return `${name} · ${price}`;
}

export type FeatureValue = boolean | string;

export type AiEmployeeFeature = {
  id: string;
  label: string;
  tooltip?: string;
  values: Record<AiEmployeePlanId, FeatureValue>;
};

export type AiEmployeeFeatureSection = {
  id: string;
  title: string;
  features: AiEmployeeFeature[];
};

export type AiEmployeePlan = {
  id: AiEmployeePlanId;
  name: string;
  priceLabel: string;
  priceSubLabel?: string;
  tagline: string;
  ctaLabel: string;
  /** Hire wizard plan query, or contact for enterprise. */
  ctaKind: "hire" | "contact";
  popular?: boolean;
};

export const HUMAN_IN_THE_LOOP_TOOLTIP =
  "Human-in-the-Loop™ lets your team take over conversations across phone, website chat, SMS, email, video calls, WhatsApp, and Messenger—with channel-specific handoff behavior and without losing context.";

export const AI_EMPLOYEE_PLANS: AiEmployeePlan[] = [
  {
    id: "intern",
    name: "Intern",
    priceLabel: "$49",
    priceSubLabel: "/month",
    tagline: "Website Chat and Voice (WebRTC). 15-day trial, then 250 credits / month.",
    ctaLabel: "Start 15-day trial",
    ctaKind: "hire",
  },
  {
    id: "associate",
    name: "Associate",
    priceLabel: "$99",
    priceSubLabel: "/month",
    tagline: "Expanded channels. 15-day trial, then 2,000 credits / month.",
    ctaLabel: "Start 15-day trial",
    ctaKind: "hire",
  },
  {
    id: "professional",
    name: "Professional",
    priceLabel: "$199",
    priceSubLabel: "/month",
    tagline: "Advanced channels and integrations. 15-day trial, then 10,000 credits / month.",
    ctaLabel: "Start 15-day trial",
    ctaKind: "hire",
    popular: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    priceLabel: "Custom",
    tagline: "Custom AI employees, routing, and volume for your organization.",
    ctaLabel: "Contact Sales",
    ctaKind: "contact",
  },
];

export const AI_EMPLOYEE_FEATURE_SECTIONS: AiEmployeeFeatureSection[] = [
  {
    id: "usage",
    title: "Usage",
    features: [
      {
        id: "conversation_credits",
        label: "Conversation Credits",
        tooltip: "Included monthly conversation volume before overage. Enterprise includes custom volume.",
        values: {
          intern: "250 Credits / mo",
          associate: "2,000 Credits / mo",
          professional: "10,000 Credits / mo",
          enterprise: "Custom",
        },
      },
    ],
  },
  {
    id: "ai_employee",
    title: "My Profile",
    features: [
      {
        id: "dedicated_phone",
        label: "Agent Phone Number",
        values: {
          intern: false,
          associate: true,
          professional: true,
          enterprise: true,
        },
      },
      {
        id: "dedicated_email",
        label: "Agent Email Address",
        values: {
          intern: false,
          associate: true,
          professional: true,
          enterprise: true,
        },
      },
      {
        id: "custom_agent_name",
        label: "Custom Agent Name",
        values: {
          intern: true,
          associate: true,
          professional: true,
          enterprise: true,
        },
      },
      {
        id: "business_branding",
        label: "SiteGlue AI Branding",
        values: {
          intern: true,
          associate: true,
          professional: true,
          enterprise: true,
        },
      },
    ],
  },
  {
    id: "channels",
    title: "Communication Channels",
    features: [
      {
        id: "website_chat",
        label: "Website Chat",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "web_voice",
        label: "Voice (WebRTC)",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "phone_calls",
        label: "Phone Calls",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
      {
        id: "sms",
        label: "SMS",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
      {
        id: "email_channel",
        label: "Email",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
      {
        id: "video_calls",
        label: "Video Calls",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
      {
        id: "whatsapp",
        label: "WhatsApp",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
      {
        id: "messenger",
        label: "Messenger",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
    ],
  },
  {
    id: "skills",
    title: "AI Skills",
    features: [
      {
        id: "hitl",
        label: "Human-in-the-Loop™",
        tooltip: HUMAN_IN_THE_LOOP_TOOLTIP,
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "human_handoff",
        label: "Team Handoff™",
        tooltip: "Route visitors to the right teammate with full conversation context.",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "team_takeover",
        label: "Team Takeover™",
        tooltip: "Live takeover from your inbox while the visitor stays in the same thread.",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "team_handoff_workflows",
        label: "Team Handoff Workflows",
        tooltip: "Configure handoff rules and workflows so the right teammate is notified with full context.",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "call_routing_workflows",
        label: "Call Routing Workflows",
        tooltip: "Route and transfer calls with whisper context to the right staff member.",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
      {
        id: "basic_scheduling",
        label: "Basic Appointments and Scheduling",
        tooltip: "Share availability links and collect preferred times for your team to confirm.",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
      {
        id: "advanced_scheduling",
        label: "Advanced Appointments and Scheduling",
        tooltip: "Book directly into calendars and practice systems (Calendly, Google Calendar, EMR/MCP).",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
      {
        id: "call_routing",
        label: "Call Routing Workflow",
        tooltip: "Advanced call routing with department rules, after-hours handling, and staff whisper context.",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
      {
        id: "emr",
        label: "EMR Integration",
        tooltip: "Connect practice systems (e.g. Juvonno, NexHealth) for patient lookup and booking.",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
      {
        id: "crm",
        label: "CRM Integration",
        values: { intern: false, associate: true, professional: true, enterprise: true },
      },
    ],
  },
  {
    id: "knowledge",
    title: "Knowledge",
    features: [
      {
        id: "basic_kb",
        label: "Basic Knowledge Base",
        tooltip: "Train your AI employee with FAQs, website content, and core organization facts.",
        values: { intern: true, associate: true, professional: true, enterprise: true },
      },
      {
        id: "advanced_kb",
        label: "Advanced Knowledge Base",
        tooltip: "Multi-source RAG, documents, and deeper retrieval for complex answers.",
        values: { intern: false, associate: false, professional: true, enterprise: true },
      },
    ],
  },
  {
    id: "organization",
    title: "Organization",
    features: [
      {
        id: "team_members",
        label: "Team Members",
        tooltip: "Included Human-in-the-Loop™ team member seats for handoff and takeover.",
        values: {
          intern: "1",
          associate: "3",
          professional: "5",
          enterprise: "Unlimited",
        },
      },
      {
        id: "locations",
        label: "Locations",
        tooltip: "Organization locations your AI Employee can represent.",
        values: {
          intern: "1",
          associate: "3",
          professional: "5",
          enterprise: "Unlimited",
        },
      },
      {
        id: "departments",
        label: "Departments",
        tooltip: "Departments available for routing and organization structure.",
        values: {
          intern: "1",
          associate: "3",
          professional: "5",
          enterprise: "Unlimited",
        },
      },
    ],
  },
];

export const AI_BUILT_FOR_HUMANS_LINE = "AI Built for Humans";

/** Customer-facing plan highlights for Trish intake recommendations. */
export function summarizePlanFeaturesForRecommendation(
  planId: AiEmployeePlanId,
): readonly string[] {
  switch (planId) {
    case "intern":
      return [
        "Website Chat & Voice (WebRTC)",
        "Human-in-the-Loop™, Team Handoff™, and Team Takeover™",
        "Basic Knowledge Base",
        "250 conversation credits/mo",
      ];
    case "associate":
      return [
        "Website Chat, Phone Calls, Voice (WebRTC), Email & Video Calls",
        "Dedicated phone number and email",
        "Basic Appointments, Call Routing Workflows, CRM, and Human-in-the-Loop™",
        "2,000 conversation credits/mo",
      ];
    case "professional":
      return [
        "All channels including SMS, WhatsApp & Messenger",
        "Advanced Appointments, EMR, and CRM integration",
        "Call Routing Workflow, Team Takeover™, and Advanced Knowledge Base",
        "10,000 conversation credits/mo",
      ];
    case "enterprise":
      return [
        "Custom AI employees, routing, and conversation volume",
        "Enterprise scheduling and integrations",
        "Dedicated onboarding and support",
      ];
  }
}

export function formatPlanFeaturesBulletBlock(planId: AiEmployeePlanId): string {
  return summarizePlanFeaturesForRecommendation(planId)
    .map((feature) => `- ${feature}`)
    .join("\n");
}
